// «Сервер просит дополнить»: a change refused for missing fields or a reason (a stage's rules, R-C14; the setting, R-C15) asks for them and is sent again.

import { useState } from 'react';
import type { CommandError, FieldDef, Id } from '@confdeck/core-shared';
import type { SyncClient } from '../sync/client.ts';
import type { PendingTx } from '../sync/store.ts';
import { useMeta, useRecord } from '../sync/react.tsx';
import { Button, Dialog, TextArea, Field } from '../ui/primitives.tsx';
import { Prop, propsClass } from './RecordView.tsx';
import s from './fix.module.css';

/** The key of the reason in a refusal (server: stage-rules.ts): not a field, asked apart from the fields. */
const REASON_KEY = '$reason';

type Next = Pick<PendingTx, 'command' | 'input' | 'ops' | 'reason'>;
export interface FixRequest { tx: PendingTx; error: CommandError; recordId: Id; fields: string[]; reason: string | null; resolve: (next: Next | null) => void }

/** Whether a refusal can be fixed by filling fields of the record the change was about and giving a reason. */
export function fixable(client: SyncClient, tx: PendingTx, error: CommandError): Omit<FixRequest, 'resolve'> | null {
  if (error.code !== 'invalid' || !error.fields) return null;
  const input = tx.input as { id?: Id };
  const rec = input.id ? client.store.record(input.id) : undefined;
  const keys = Object.keys(error.fields);
  const fields = keys.filter((k) => k !== REASON_KEY);
  // Only a reason asked (an archive of several records, an app's action): no record fields to show, the reason is enough.
  if (!fields.length && error.fields[REASON_KEY]) return { tx, error, recordId: rec?.id ?? '', fields: [], reason: error.fields[REASON_KEY] };
  if (!rec || !keys.length || !fields.every((k) => client.store.meta.field(rec.typeId, k))) return null;
  return { tx, error, recordId: rec.id, fields, reason: error.fields[REASON_KEY] ?? null };
}

export function FixDialog({ req, onClose }: { req: FixRequest; onClose: () => void }) {
  const meta = useMeta();
  const rec = useRecord(req.recordId);
  const [reason, setReason] = useState('');
  const fields = rec ? req.fields.map((k) => meta.field(rec.typeId, k)).filter((f): f is FieldDef => !!f) : [];
  // Fields are edited in place, each as its own change sent before the retry; the server checks them again on the retry.
  const filled = (f: FieldDef) => { const v = rec?.data[f.key]; return f.fieldType === 'relation' || !(v === undefined || v === null || v === '' || (Array.isArray(v) && !v.length)); };
  const ready = (!req.reason || !!reason.trim()) && fields.every(filled);
  const close = (next: Next | null) => { req.resolve(next); onClose(); };
  const send = () => close({ command: req.tx.command, input: req.tx.input, ops: req.tx.ops, ...(req.reason ? { reason: reason.trim() } : req.tx.reason ? { reason: req.tx.reason } : {}) });
  return (
    <Dialog open onOpenChange={(o) => { if (!o) close(null); }} label={req.error.message}>
      <div className={s.box} onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && ready) send(); }}>
        <h2 className={s.title}>{req.error.message}</h2>
        {rec && fields.length > 0 && <dl className={propsClass}>{fields.map((f) => <Prop key={f.id} rec={rec} field={f} />)}</dl>}
        {req.reason && (
          <Field label="Причина">
            <TextArea autoFocus={!fields.length} rows={3} value={reason} placeholder={req.reason} onChange={(e) => setReason(e.target.value)} />
          </Field>
        )}
        <div className={s.actions}><Button onClick={() => close(null)}>Отмена</Button><Button variant="primary" disabled={!ready} onClick={send}>Сохранить</Button></div>
      </div>
    </Dialog>
  );
}
